import React from 'react'
import clsx from 'clsx'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  children: React.ReactNode
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading,
  disabled,
  children,
  className,
  style,
  ...props
}) => {
  const base = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'

  const variants = {
    primary: 'text-white hover:opacity-90 focus-visible:ring-[var(--accent)]',
    secondary: 'hover:opacity-80 focus-visible:ring-[var(--accent)]',
    ghost: 'hover:opacity-70 focus-visible:ring-[var(--accent)]',
    danger: 'text-white hover:opacity-90 focus-visible:ring-[var(--danger)]',
  }

  const sizes = {
    sm: 'h-8 px-3 text-[13px] gap-1.5',
    md: 'h-10 px-4 text-[14px] gap-2',
    lg: 'h-12 px-6 text-[15px] gap-2',
  }

  return (
    <button
      className={clsx(base, variants[variant], sizes[size], className)}
      disabled={disabled || loading}
      style={{
        backgroundColor:
          variant === 'primary' ? 'var(--accent)' :
          variant === 'danger' ? 'var(--danger)' :
          variant === 'secondary' ? 'var(--bg-tertiary)' :
          'transparent',
        color:
          variant === 'primary' ? 'var(--accent-on)' : variant === 'danger' ? 'var(--bg-primary)' :
          'var(--text-primary)',
        opacity: disabled || loading ? 0.5 : undefined,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        ...style,
      }}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </button>
  )
}
